/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/catalog/ProductPacks.tsx
 * ----------------------------------------------------------------------------
 * Página “Packs do Produto”
 * - Autocomplete de produtos (código/EAN/nome) com múltiplos fallbacks de rota.
 * - Mostra disponibilidade total em estoque para cada componente.
 * - Corrigido: nunca duplica /api/api — usa apiUrl() para resolver a base.
 * - Tolerante a esquemas: aceita name/name1, sku/barcode/ean/gtin etc.
 * ========================================================================== */

import React, { useEffect, useMemo, useRef, useState } from "react"
import { Plus, RefreshCw, Trash2, Search } from "lucide-react"

// ============================================================================
// Mini HTTP client (com resolvedor de base e tolerância a 404)
// ============================================================================

const API_BASE =
    (import.meta.env as any)?.VITE_API_BASE ??
    (import.meta.env as any)?.VITE_API_BASE_URL ??
    "/api"

/** Junta a base da API com um caminho evitando `//` e, principalmente, `/api/api`. */
function apiUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) return path
    const base = String(API_BASE || "/api").replace(/\/+$/, "") // sem barra no final
    const p = path.startsWith("/") ? path : `/${path}`
    // Se o caminho já começa pela base (ex.: /api/...), devolve como está.
    if (p === base || p.startsWith(`${base}/`)) return p
    return `${base}${p}`
}

function authHeader(): Record<string, string> {
    const tok =
        localStorage.getItem("movatic_token") ||
        localStorage.getItem("access") ||
        localStorage.getItem("token")
    return tok ? { Authorization: `Bearer ${tok}` } : {}
}

function normalizeHeaders(h?: HeadersInit): Record<string, string> {
    const out: Record<string, string> = {}
    if (!h) return out
    if (h instanceof Headers) h.forEach((v, k) => (out[k] = String(v)))
    else if (Array.isArray(h)) h.forEach(([k, v]) => (out[k] = String(v)))
    else Object.assign(out, h as Record<string, string>)
    return out
}

async function http<T>(url: string, init?: RequestInit): Promise<T> {
    const base: Record<string, string> = { "Content-Type": "application/json", ...authHeader() }
    const headers: HeadersInit = { ...base, ...normalizeHeaders(init?.headers) }
    const final = apiUrl(url)

    const res = await fetch(final, { ...init, headers, credentials: "include" })
    if (!res.ok) {
        // Propaga o erro para que possamos tentar os próximos fallbacks (404 etc.)
        const text = await res.text().catch(() => "")
        const err = new Error(text || `${res.status} ${res.statusText}`)
            ; (err as any).status = res.status
        throw err
    }
    if (res.status === 204) return undefined as unknown as T
    return (await res.json()) as T
}

/** Tenta, em ordem, uma lista de rotas alternativas. Interrompe no primeiro 2xx. */
async function tryGet<T>(paths: string[], params?: Record<string, any>) {
    const q =
        params && Object.keys(params).length
            ? "?" +
            Object.entries(params)
                .filter(([, v]) => v !== undefined && v !== null && v !== "")
                .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
                .join("&")
            : ""
    let last: any
    for (const p of paths) {
        try {
            const data = await http<T>(`${p}${q}`)
            return { used: p, data }
        } catch (e: any) {
            last = e
            // Continua tentando em 404/500 — objetivo é achar algum que funcione.
        }
    }
    throw last
}

// ============================================================================
// Endpoints candidatos
// ============================================================================

/** Rotas possíveis para buscar produtos (depende de qual app está exposto). */
const PRODUCT_SEARCH_BASES = [
    "/product/products/",
    "/product/product/",
    "/products/",
    "/product_api/products/",
]

/** Rotas possíveis para consultar estoque (lista de StockItem). */
const STOCK_BASES = ["/inventory/stock-items/", "/inventory/stock/", "/stock-items/"]

// ============================================================================
// Tipos
// ============================================================================

type ProductLite = {
    id: number
    code: string
    ean13?: string | null
    name1: string
    uom?: string | { code: string }
}

type StockItem = { qty?: number; uom?: string | { code: string } }

type StockResponse =
    | { results?: StockItem[]; total?: { qty?: number; uom?: string | { code: string } } }
    | StockItem[]
    | { items?: StockItem[]; sum?: number }

type ComponentRow = {
    id: string
    product?: ProductLite
    qty: number
    available?: number
    uom?: string
}

// ============================================================================
// Autocomplete de produtos (com múltiplos fallbacks + /inventory/scan/summary)
// ============================================================================

function ProductAutocomplete({
    value,
    onSelect,
}: {
    value?: ProductLite
    onSelect: (p: ProductLite) => void
}) {
    const [term, setTerm] = useState("")
    const [open, setOpen] = useState(false)
    const [busy, setBusy] = useState(false)
    const [items, setItems] = useState<ProductLite[]>([])
    const [err, setErr] = useState<string | null>(null)
    const refWrap = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const onClick = (ev: MouseEvent) => {
            if (!refWrap.current) return
            if (!refWrap.current.contains(ev.target as any)) setOpen(false)
        }
        document.addEventListener("click", onClick)
        return () => document.removeEventListener("click", onClick)
    }, [])

    async function searchProducts(q: string) {
        setErr(null)
        if (!q || q.trim().length < 2) {
            setItems([])
            return
        }
        setBusy(true)
        try {
            // 1) Tenta nas rotas de catálogo conhecidas
            const { data } = await tryGet<any>(PRODUCT_SEARCH_BASES, { search: q, page_size: 10 })
            const raw: any[] = Array.isArray(data) ? data : data?.results || []
            const mapped: ProductLite[] = raw.map((p: any) => ({
                id: Number(p.id),
                code: String(p.code ?? p.sku ?? p.barcode ?? p.ean ?? p.gtin ?? ""),
                ean13: p.ean13 ?? p.barcode ?? p.ean ?? null,
                name1: String(p.name1 ?? p.name ?? p.description ?? ""),
                uom: typeof p.uom === "object" ? p.uom?.code : p.uom,
            }))
            if (mapped.length > 0) {
                setItems(mapped)
                return
            }
            // 2) Se nada voltar, tenta resumir via scan (buscar um produto único)
            const scan: any = await http(`/inventory/scan/` + `?q=${encodeURIComponent(q)}&mode=summary`)
            if (scan?.found && scan?.product?.id) {
                const p = scan.product
                setItems([
                    {
                        id: Number(p.id),
                        code: String(p.code ?? ""),
                        ean13: null,
                        name1: String(p.name ?? ""),
                        uom: p.uom,
                    },
                ])
            } else {
                setItems([])
            }
        } catch (e: any) {
            setItems([])
            setErr("Não foi possível buscar produtos.")
            // console.debug("Product search error:", e)
        } finally {
            setBusy(false)
        }
    }

    useEffect(() => {
        if (value) setTerm(`${value.code} — ${value.name1}`)
    }, [value?.id])

    return (
        <div ref={refWrap} className="relative">
            <div className="flex items-center gap-2">
                <Search size={16} className="opacity-70" />
                <input
                    className="input w-[420px]"
                    placeholder="Buscar produto (código, EAN ou nome)…"
                    value={term}
                    onChange={(e) => {
                        const v = e.target.value
                        setTerm(v)
                        setOpen(true)
                        searchProducts(v)
                    }}
                    onFocus={() => {
                        setOpen(true)
                        if (term && items.length === 0) searchProducts(term)
                    }}
                />
            </div>

            {open && (
                <div className="absolute z-30 mt-1 w-[520px] max-h-[260px] overflow-auto rounded border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow">
                    {busy ? (
                        <div className="px-3 py-2 text-sm text-neutral-500">Buscando…</div>
                    ) : err ? (
                        <div className="px-3 py-2 text-sm text-red-500">{err}</div>
                    ) : items.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-neutral-500">Sem resultados.</div>
                    ) : (
                        items.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => {
                                    onSelect(p)
                                    setOpen(false)
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                            >
                                <div className="font-mono text-xs">
                                    {p.code}
                                    {p.ean13 ? ` • ${p.ean13}` : ""}
                                </div>
                                <div className="text-sm">{p.name1}</div>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    )
}

// ============================================================================
// Utilitários de estoque
// ============================================================================

function uomCode(u?: string | { code: string } | null): string | undefined {
    if (!u) return undefined
    return typeof u === "string" ? u : u.code
}

function sumStock(resp: StockResponse): { qty: number; uom?: string } {
    if (resp && typeof resp === "object" && "total" in resp && (resp as any).total) {
        const t = (resp as any).total
        return { qty: Number(t.qty || 0), uom: uomCode(t.uom) }
    }
    if (resp && typeof resp === "object" && "results" in resp) {
        const arr: StockItem[] = (resp as any).results || []
        const qty = arr.reduce((acc, it) => acc + Number(it.qty || 0), 0)
        const anyU = arr.find((x) => x.uom)?.uom
        return { qty, uom: uomCode(anyU) }
    }
    if (Array.isArray(resp)) {
        const qty = resp.reduce((acc, it) => acc + Number(it.qty || 0), 0)
        const anyU = resp.find((x) => x.uom)?.uom
        return { qty, uom: uomCode(anyU) }
    }
    if (resp && typeof resp === "object" && "items" in resp) {
        const arr: StockItem[] = (resp as any).items || []
        const qty = arr.reduce((acc, it) => acc + Number(it.qty || 0), 0)
        const anyU = arr.find((x) => x.uom)?.uom
        return { qty, uom: uomCode(anyU) }
    }
    return { qty: 0 }
}

async function fetchAvailability(p: ProductLite): Promise<{ qty: number; uom?: string }> {
    // Busca por “search=” para contemplar todos os campos configurados no backend
    try {
        const bySearch = await tryGet<StockResponse>(STOCK_BASES, {
            search: p.code,
            page_size: 200,
        })
        const s = sumStock(bySearch.data)
        if (s.qty !== 0) return s
    } catch {
        /* tenta os próximos */
    }
    // Fallback: tenta por product=id
    try {
        const byId = await tryGet<StockResponse>(STOCK_BASES, { product: p.id, page_size: 200 })
        return sumStock(byId.data)
    } catch {
        /* noop */
    }
    return { qty: 0 }
}

// ============================================================================
// Página principal
// ============================================================================

type ComponentRowState = ComponentRow

export default function ProductPacks() {
    const [kitCode, setKitCode] = useState("")
    const [kitName, setKitName] = useState("")
    const [kitLabel, setKitLabel] = useState("")
    const [notes, setNotes] = useState("")
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)

    const [rows, setRows] = useState<ComponentRowState[]>([{ id: crypto.randomUUID(), qty: 1 }])

    const totalComponentes = useMemo(() => rows.filter((r) => r.product).length, [rows])

    function addRow() {
        setRows((prev) => [...prev, { id: crypto.randomUUID(), qty: 1 }])
    }
    function delRow(id: string) {
        setRows((prev) => prev.filter((r) => r.id !== id))
    }

    async function onSelectProduct(rowId: string, p: ProductLite) {
        setRows((prev) =>
            prev.map((r) => (r.id === rowId ? { ...r, product: p, available: undefined, uom: undefined } : r)),
        )
        try {
            const { qty, uom } = await fetchAvailability(p)
            setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, available: qty, uom } : r)))
        } catch {
            setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, available: 0 } : r)))
        }
    }

    async function refreshRow(rowId: string) {
        const row = rows.find((r) => r.id === rowId)
        if (!row?.product) return
        try {
            const { qty, uom } = await fetchAvailability(row.product)
            setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, available: qty, uom } : r)))
        } catch {
            /* noop */
        }
    }

    function changeQty(rowId: string, v: number) {
        setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, qty: v } : r)))
    }

    function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
        const f = e.target.files?.[0]
        if (!f) return
        const url = URL.createObjectURL(f)
        setPreviewUrl(url)
    }

    async function onSave() {
        if (!kitCode.trim() || !kitName.trim()) return alert("Informe o código e o nome do kit.")
        const components = rows
            .filter((r) => r.product && r.qty > 0)
            .map((r) => ({ product: r.product!.id, qty: r.qty }))

        if (components.length === 0) return alert("Adicione pelo menos um componente ao kit.")

        const payload = {
            code: kitCode.trim(),
            name: kitName.trim(),
            label_code: kitLabel.trim() || null,
            notes: notes.trim() || null,
            components,
        }

        console.log("Payload do pack:", payload)
        alert("Simulação: pack pronto para enviar (ver console).")
        // Ex.: await http('/product/packs/', { method: 'POST', body: JSON.stringify(payload) })
    }

    return (
        <div className="p-4">
            <h2 className="text-lg font-semibold">Packs do Produto</h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                Monte um kit com múltiplos componentes. O estoque total de cada componente é consultado automaticamente.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="card space-y-3">
                    <div>
                        <label className="block text-sm mb-1">Código do kit (SKU/EAN)</label>
                        <input className="input" value={kitCode} onChange={(e) => setKitCode(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm mb-1">Nome do kit</label>
                        <input className="input" value={kitName} onChange={(e) => setKitName(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm mb-1">Observações de embalagem</label>
                        <textarea className="input min-h-[120px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm mb-1">Código de rótulo (opcional)</label>
                        <input className="input" value={kitLabel} onChange={(e) => setKitLabel(e.target.value)} />
                    </div>
                </div>

                <div className="card">
                    <div className="text-sm mb-2">Foto de referência (opcional)</div>
                    <div className="flex items-center gap-3">
                        <input type="file" accept="image/*" onChange={onPickImage} />
                        {previewUrl ? (
                            <img
                                src={previewUrl}
                                alt="preview"
                                className="h-28 w-28 object-cover rounded border border-neutral-200 dark:border-neutral-800"
                            />
                        ) : (
                            <div className="h-28 w-28 rounded border border-dashed grid place-items-center text-xs text-neutral-500">
                                Pré-visualização
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="card mt-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="font-medium">Componentes do kit ({totalComponentes})</div>
                    <button className="btn" onClick={addRow}>
                        <Plus size={16} className="mr-2" /> Adicionar componente
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-neutral-200 dark:border-neutral-800">
                                <th className="py-2 px-2">Produto</th>
                                <th className="py-2 px-2 w-32">Quantidade</th>
                                <th className="py-2 px-2 w-48">Disponível</th>
                                <th className="py-2 px-2 w-24 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-neutral-200/60 dark:border-neutral-800/60">
                                    <td className="py-2 px-2">
                                        <ProductAutocomplete value={r.product} onSelect={(p) => onSelectProduct(r.id, p)} />
                                        {r.product ? (
                                            <div className="mt-1 text-xs text-neutral-500">
                                                <span className="font-mono">{r.product.code}</span> — {r.product.name1}
                                            </div>
                                        ) : null}
                                    </td>
                                    <td className="py-2 px-2">
                                        <input
                                            type="number"
                                            min={0}
                                            className="input w-28"
                                            value={r.qty}
                                            onChange={(e) => changeQty(r.id, Math.max(0, Number(e.target.value || 0)))}
                                        />
                                    </td>
                                    <td className="py-2 px-2">
                                        {r.product ? (
                                            <div className="inline-flex items-center gap-2">
                                                <span className="badge">
                                                    {typeof r.available === "number" ? r.available : "—"} {r.uom || ""}
                                                </span>
                                                <button
                                                    className="iconbtn"
                                                    title="Atualizar disponibilidade"
                                                    onClick={() => refreshRow(r.id)}
                                                >
                                                    <RefreshCw size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="opacity-60">—</span>
                                        )}
                                    </td>
                                    <td className="py-2 px-2 text-right">
                                        <button className="iconbtn text-red-500" onClick={() => delRow(r.id)} title="Remover">
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="py-6 text-center text-neutral-500">
                                        Nenhum componente adicionado.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mt-3 flex items-center justify-end">
                    <button className="btn" onClick={onSave}>
                        Salvar Pack
                    </button>
                </div>
            </div>
        </div>
    )
}
